import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { formatDateTime } from "../../utils/format";
import { Reveal, Eyebrow } from "../../components/ui";
import { getMyMatches } from "../../services/chatExtensionService";
import type { ChatMatch } from "../../types/chatExtension";

export default function MyChats() {
  const navigate = useNavigate();
  const [matches, setMatches] = useState<ChatMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!localStorage.getItem("accessToken")) {
      navigate("/pay?returnTo=/my-chats");
      return;
    }

    async function load() {
      try {
        const data = await getMyMatches();
        setMatches(data);
      } catch (e: any) {
        setError(e?.message || "Could not load your chats.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [navigate]);

  return (
    <main className="bg-white px-[5%] pt-[140px] pb-20 lg:pb-[100px] min-h-screen">
      <div className="max-w-[800px] mx-auto">
        <Reveal>
          <Eyebrow>Your Connections</Eyebrow>
          <h1 className="text-3xl font-bold tracking-tight mb-8">My Chats</h1>
        </Reveal>

        {loading && (
          <div className="text-center py-16">
            <div className="w-10 h-10 border-2 border-black/10 border-t-rose rounded-full animate-spin mx-auto mb-6" />
            <p className="text-[15px] text-muted">Loading your matches…</p>
          </div>
        )}

        {error && (
          <div className="p-6 rounded-2xl border border-rose/20 bg-blush text-center">
            <p className="text-[14px] text-rose-dark">{error}</p>
          </div>
        )}

        {!loading && !error && matches.length === 0 && (
          <div className="text-center py-16 border border-black/10 rounded-3xl bg-neutral-50">
            <p className="text-[15px] text-muted mb-4">
              You don't have any active or expired chats yet.
            </p>
            <Link
              to="/account"
              className="text-sm font-semibold text-black border border-black/10 px-5 py-2.5 rounded-full hover:bg-white transition-colors"
            >
              Back to Account
            </Link>
          </div>
        )}

        <div className="space-y-4">
          {matches.map((match) => {
            const isExpired = new Date(match.expiresAt).getTime() < Date.now();
            // In my-matches, user2 is always the person they matched with
            const partner = match.user2;
            const photoUrl = partner.profile?.photos?.[0]?.url;

            return (
              <Reveal key={match.id}>
                <div
                  className={`p-5 rounded-2xl border flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-all ${
                    isExpired
                      ? "border-rose/30 bg-blush/30"
                      : "border-black/10 bg-white"
                  }`}
                >
                  {/* Profile Info */}
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full overflow-hidden bg-neutral-200 shrink-0">
                      {photoUrl ? (
                        <img
                          src={photoUrl}
                          alt={partner.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-lg font-bold text-neutral-400">
                          {partner.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-black flex items-center gap-2">
                        {partner.name}
                        {partner.is_verified && (
                          <span
                            className="text-blue-500 text-sm"
                            title="Verified"
                          >
                            ✓
                          </span>
                        )}
                      </h3>
                      <p className="text-[13px] text-muted mt-0.5">
                        {isExpired ? (
                          <span className="text-rose-dark font-medium">
                            Expired on {formatDateTime(match.expiresAt)}
                          </span>
                        ) : (
                          <span className="text-emerald-600 font-medium">
                            Active until {formatDateTime(match.expiresAt)}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="shrink-0">
                    <button
                      onClick={() => navigate(`/extend/${match.id}`)}
                      className={`w-full sm:w-auto px-6 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                        isExpired
                          ? "bg-rose text-white hover:bg-rose-dark"
                          : "bg-black/5 text-black hover:bg-black/10"
                      }`}
                    >
                      {isExpired ? "Extend Chat" : "Add Time"}
                    </button>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </main>
  );
}
